/**
 * Keys and accounts panel — your own keys, subscriptions and aggregators,
 * with Test and data terms beside each key.
 *
 * Reads (each time-limited, each isolated to this panel):
 *  - The providers dashboard (one engine request): every provider the engine
 *    knows, with key status, CLI detection and test support. Subscriptions
 *    appear here as engine provider cards — nothing is listed by hand.
 *  - The combined provider-settings read (one engine request, shared with
 *    Spending and usage): per-provider data terms. It reaches production
 *    with the engine pin bump; until the live engine serves it, the
 *    data-terms block is honest "Coming soon" and the rest of the panel is
 *    unaffected.
 */

import React, { useState } from 'react';
import { useSWRConfig } from 'swr';
import {
  deleteLLMProviderKey,
  getLLMProviderDashboard,
  getProviderSettings,
  saveLLMProviderKey,
  testLLMProvider,
  type LLMProviderCard,
  type ProviderSettingsEntry,
} from '@/api/llmSettings';
import { ComingSoonBlock, RouterReadError, RouterReadLoading } from './PanelErrorBoundary';
import { useRouterRead } from './useRouterRead';

const DASHBOARD_SWR_KEY = '/api/v1/settings/llm/providers';
export const PROVIDER_SETTINGS_SWR_KEY = '/api/v1/settings/llm/provider-settings';

type TestState = 'running' | 'pass' | 'fail' | null;

function statusLabel(card: LLMProviderCard): string {
  if (card.status === 'ready') return 'ready';
  if (card.status === 'unavailable') return 'unavailable';
  return 'not configured';
}

const ProviderRow: React.FC<{
  card: LLMProviderCard;
  settings: ProviderSettingsEntry | undefined;
  settingsFailed: boolean;
  testState: TestState;
  testMessage: string | null;
  busy: boolean;
  onTest: () => void;
  onSaveKey: (apiKey: string) => void;
  onRemoveKey: () => void;
}> = ({ card, settings, settingsFailed, testState, testMessage, busy, onTest, onSaveKey, onRemoveKey }) => {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const name = card.display_name;

  return (
    <div
      data-testid={`mr-key-row-${card.provider_id}`}
      style={{
        border: '1px solid var(--sp-border)',
        borderRadius: 'var(--sp-radius-lg)',
        background: 'var(--sp-bg-elev)',
        padding: '10px var(--sp-space-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-space-3)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-text)' }}>{name}</div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
            {card.model_display_name ?? card.model_id ?? card.provider_id}
          </div>
        </div>
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: card.status === 'ready' ? 'var(--sp-ok)' : card.status === 'unavailable' ? 'var(--sp-error)' : 'var(--sp-text-dim)',
          }}
        >
          {statusLabel(card)}
        </span>
        {card.cli_detected ? (
          <span className="mono" style={{ fontSize: 10, color: 'var(--sp-accent)' }}>
            CLI detected
          </span>
        ) : null}
        {card.uses_env_key && !card.has_stored_key ? (
          <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
            runtime key
          </span>
        ) : null}
        <button
          type="button"
          className="sp-btn"
          disabled={!card.supports_test || testState === 'running'}
          onClick={onTest}
          aria-label={`Test ${name}`}
        >
          {testState === 'running' ? 'Testing…' : testState === 'pass' ? '✓ Pass' : testState === 'fail' ? '× Fail' : 'Test'}
        </button>
        <button
          type="button"
          className="sp-btn"
          disabled={busy}
          onClick={() => setExpanded((open) => !open)}
          aria-label={`${expanded ? 'Close key entry for' : card.has_stored_key ? 'Edit key for' : 'Add key for'} ${name}`}
        >
          {expanded ? 'Close' : card.has_stored_key ? 'Edit key' : 'Add key'}
        </button>
      </div>

      {testMessage ? (
        <div className="mono" style={{ fontSize: 10, color: testState === 'fail' ? 'var(--sp-error)' : 'var(--sp-text-muted)' }}>
          {testMessage}
        </div>
      ) : null}
      {card.latest_error ? (
        <div className="mono" style={{ fontSize: 10, color: 'var(--sp-warn)' }}>
          Last engine test error: {card.latest_error}
        </div>
      ) : null}

      {!settingsFailed && settings?.status === 'available' ? (
        <div style={{ fontSize: 11, color: 'var(--sp-text-muted)' }}>{settings.data_choice.disclosure.text}</div>
      ) : null}
      {!settingsFailed && settings?.status === 'unavailable' ? (
        <div style={{ fontSize: 11, color: 'var(--sp-text-dim)' }}>Data terms unavailable for this provider.</div>
      ) : null}

      {expanded ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="password"
            className="sp-input"
            style={{ maxWidth: 320 }}
            data-testid={`mr-key-input-${card.provider_id}`}
            placeholder={card.has_stored_key ? '(already configured — replace)' : 'Paste the API key'}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            autoComplete="new-password"
            name={`mr-key-${card.provider_id}`}
            data-form-type="other"
            data-1p-ignore
            data-lpignore="true"
          />
          <button
            type="button"
            className="sp-btn sp-btn-primary"
            disabled={busy || !draft.trim()}
            onClick={() => {
              onSaveKey(draft.trim());
              setDraft('');
            }}
            aria-label={`Save key for ${name}`}
          >
            Save to vault
          </button>
          {card.has_stored_key ? (
            <button
              type="button"
              className="sp-btn"
              disabled={busy}
              onClick={onRemoveKey}
              aria-label={`Remove key for ${name}`}
            >
              Remove
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export const KeysAndAccountsPanel: React.FC = () => {
  const { mutate } = useSWRConfig();
  const dashboard = useRouterRead(DASHBOARD_SWR_KEY, (signal) => getLLMProviderDashboard({ signal }));
  const settings = useRouterRead(PROVIDER_SETTINGS_SWR_KEY, (signal) => getProviderSettings({ signal }));

  const [tests, setTests] = useState<Record<string, TestState>>({});
  const [testMessages, setTestMessages] = useState<Record<string, string | null>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  // A failed read with nothing to show errors the panel; a failed REFRESH
  // with rows already on screen keeps the rows — the action message above
  // the list says what happened (a committed key never reads as lost).
  if (dashboard.error && !dashboard.data) {
    return <RouterReadError error={dashboard.error} onRetry={dashboard.retry} />;
  }
  if (dashboard.isLoading) {
    return <RouterReadLoading label="Loading your providers…" />;
  }

  const groups = dashboard.data?.groups ?? [];
  const settingsFailed = Boolean(settings.error);

  const handleTest = async (card: LLMProviderCard) => {
    setTests((state) => ({ ...state, [card.provider_id]: 'running' }));
    setTestMessages((state) => ({ ...state, [card.provider_id]: null }));
    try {
      const result = await testLLMProvider(card.provider_id);
      setTests((state) => ({ ...state, [card.provider_id]: result.ok ? 'pass' : 'fail' }));
      setTestMessages((state) => ({
        ...state,
        [card.provider_id]: `${result.message} (${result.latency_ms}ms)`,
      }));
    } catch (err) {
      setTests((state) => ({ ...state, [card.provider_id]: 'fail' }));
      setTestMessages((state) => ({
        ...state,
        [card.provider_id]: err instanceof Error ? err.message : 'Test failed.',
      }));
    }
  };

  const handleSaveKey = async (card: LLMProviderCard, apiKey: string) => {
    setBusy((state) => ({ ...state, [card.provider_id]: true }));
    setActionMessage(null);
    try {
      await saveLLMProviderKey(card.provider_id, apiKey, `${card.display_name} API key`);
      // The key is committed in the vault at this point. A failed list
      // refresh must never read back as a failed save: the save reports
      // itself, and a refresh failure shows beside the list instead.
      setActionMessage(`${card.display_name} key saved.`);
      void mutate(DASHBOARD_SWR_KEY).catch(() => undefined);
    } catch (err) {
      setActionMessage(err instanceof Error ? err.message : `Could not save the ${card.display_name} key.`);
    } finally {
      setBusy((state) => ({ ...state, [card.provider_id]: false }));
    }
  };

  const handleRemoveKey = async (card: LLMProviderCard) => {
    setBusy((state) => ({ ...state, [card.provider_id]: true }));
    setActionMessage(null);
    try {
      await deleteLLMProviderKey(card.provider_id);
      setActionMessage(`${card.display_name} key removed.`);
      void mutate(DASHBOARD_SWR_KEY).catch(() => undefined);
    } catch (err) {
      setActionMessage(err instanceof Error ? err.message : `Could not remove the ${card.display_name} key.`);
    } finally {
      setBusy((state) => ({ ...state, [card.provider_id]: false }));
    }
  };

  return (
    <div data-testid="models-routing-keys-panel" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-space-3)' }}>
      {actionMessage ? (
        <div
          role="status"
          style={{
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            padding: 'var(--sp-space-3)',
            fontSize: 12,
            color: 'var(--sp-text)',
          }}
        >
          {actionMessage}
        </div>
      ) : null}

      {dashboard.error ? (
        <div
          data-testid="mr-refresh-error"
          role="alert"
          style={{
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            padding: 'var(--sp-space-3)',
            fontSize: 12,
            color: 'var(--sp-text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ flex: 1 }}>
            The provider list could not be refreshed: {dashboard.error.message} The rows below are
            the last good read.
          </span>
          <button type="button" className="sp-btn" onClick={dashboard.retry}>
            Retry
          </button>
        </div>
      ) : null}

      {groups.length === 0 ? (
        <div className="sp-empty" style={{ padding: '24px 12px' }}>
          The engine is not advertising any providers yet.
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
              {group.label}
            </div>
            {group.providers.map((card) => (
              <ProviderRow
                key={card.provider_id}
                card={card}
                settings={settings.data?.providers[card.provider_id]}
                settingsFailed={settingsFailed}
                testState={tests[card.provider_id] ?? null}
                testMessage={testMessages[card.provider_id] ?? null}
                busy={Boolean(busy[card.provider_id])}
                onTest={() => void handleTest(card)}
                onSaveKey={(apiKey) => void handleSaveKey(card, apiKey)}
                onRemoveKey={() => void handleRemoveKey(card)}
              />
            ))}
          </section>
        ))
      )}

      {settingsFailed ? (
        <ComingSoonBlock
          title="Data terms beside each key"
          body="The combined per-provider read that carries retention and training terms is on engine main and arrives with the next engine update. Keys, status and Test above keep working without it."
          testId="mr-data-terms-coming-soon"
        />
      ) : null}

      <ComingSoonBlock
        title="Aggregators — one key, many models"
        body="Routing services that resell inference behind a single key appear here once the engine advertises them."
        testId="mr-aggregators-coming-soon"
      />
    </div>
  );
};
